import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { CURRENCIES, formatQty, parseMoney, parseQty, unitAllowsDecimals, UNITS, type CurrencyCode } from '@tengesa/shared';
import { api } from '../lib/api';
import type { ProductDto } from '../lib/types';
import { TextInput } from './index';

/** Amount input that keeps integer cents as its value. */
export function MoneyInput({
  value,
  onChange,
  currency,
  invalid,
  id,
  autoFocus,
  placeholder,
  className,
  onEnter,
}: {
  value: number | null;
  onChange: (cents: number | null) => void;
  currency: CurrencyCode;
  invalid?: boolean;
  id?: string;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  onEnter?: () => void;
}) {
  const [text, setText] = useState(value == null ? '' : (value / 100).toFixed(2));
  const last = useRef(value);
  useEffect(() => {
    if (value !== last.current) {
      setText(value == null ? '' : (value / 100).toFixed(2));
      last.current = value;
    }
  }, [value]);
  const bad = text.trim() !== '' && parseMoney(text, currency) == null;
  return (
    <div className={clsx('relative', className)}>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted">{CURRENCIES[currency].symbol}</span>
      <TextInput
        id={id}
        inputMode="decimal"
        autoFocus={autoFocus}
        placeholder={placeholder ?? '0.00'}
        value={text}
        invalid={invalid || bad}
        className="pl-12 text-right num"
        onChange={(e) => {
          setText(e.target.value);
          const c = e.target.value.trim() === '' ? null : parseMoney(e.target.value, currency);
          last.current = c;
          onChange(c);
        }}
        onBlur={() => {
          if (value != null) setText((value / 100).toFixed(2));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onEnter) {
            e.preventDefault();
            onEnter();
          }
        }}
      />
    </div>
  );
}

/** Quantity input in milli-units; whole-number units refuse decimals. */
export function QtyInput({
  value,
  onChange,
  unit,
  invalid,
  id,
  autoFocus,
  className,
  allowZero,
  onEnter,
  'aria-label': ariaLabel,
}: {
  'aria-label'?: string;
  value: number | null;
  onChange: (milli: number | null) => void;
  unit: string;
  invalid?: boolean;
  id?: string;
  autoFocus?: boolean;
  className?: string;
  allowZero?: boolean;
  onEnter?: () => void;
}) {
  const [text, setText] = useState(value == null ? '' : formatQty(value));
  const last = useRef(value);
  useEffect(() => {
    if (value !== last.current) {
      setText(value == null ? '' : formatQty(value));
      last.current = value;
    }
  }, [value]);
  const decimals = unitAllowsDecimals(unit);
  const parse = (t: string) => {
    const q = parseQty(t);
    if (q == null || q < 0 || (!allowZero && q === 0)) return null;
    if (!decimals && q % 1000 !== 0) return null;
    return q;
  };
  const bad = text.trim() !== '' && parse(text) == null;
  const short = (UNITS as Record<string, { short: string }>)[unit]?.short ?? unit;
  return (
    <div className={clsx('relative', className)}>
      <TextInput
        id={id}
        aria-label={ariaLabel}
        inputMode={decimals ? 'decimal' : 'numeric'}
        autoFocus={autoFocus}
        value={text}
        invalid={invalid || bad}
        className="pr-10 text-right num"
        onChange={(e) => {
          setText(e.target.value);
          const q = e.target.value.trim() === '' ? null : parse(e.target.value);
          last.current = q;
          onChange(q);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onEnter) {
            e.preventDefault();
            onEnter();
          }
        }}
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted">{short}</span>
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: { value: T; label: ReactNode }[] }) {
  return (
    <div role="tablist" className="mb-5 flex gap-1 overflow-x-auto border-b border-line">
      {tabs.map((t) => (
        <button
          key={t.value}
          role="tab"
          aria-selected={value === t.value}
          onClick={() => onChange(t.value)}
          className={clsx(
            '-mb-px whitespace-nowrap border-b-2 px-3 py-2.5 text-[15px] font-medium transition-colors',
            value === t.value ? 'border-ledger-600 text-ink' : 'border-transparent text-muted hover:text-ink',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function useDebounced<T>(value: T, ms = 200): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Search-as-you-type product picker. Calls onPick with the chosen product. */
export function ProductPicker({
  onPick,
  branchId,
  sellable = true,
  stockedOnly,
  placeholder = 'Search by name, code or barcode',
  autoFocus,
  label,
}: {
  onPick: (p: ProductDto) => void;
  branchId?: string | null;
  sellable?: boolean;
  stockedOnly?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  label?: string;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const dq = useDebounced(q.trim(), 150);
  const listId = useId();
  const res = useQuery({
    queryKey: ['product-search', dq, branchId, sellable],
    queryFn: () =>
      api.get<{ items: ProductDto[] }>(
        `/api/products?q=${encodeURIComponent(dq)}&limit=12${branchId ? `&branchId=${branchId}` : ''}${sellable ? '&sellable=true' : ''}`,
      ),
    enabled: dq.length > 0,
  });
  const items = (res.data?.items ?? []).filter((p) => !stockedOnly || (p.trackStock && !p.isVariantParent));
  const pick = (p: ProductDto) => {
    onPick(p);
    setQ('');
    setOpen(false);
    setActive(0);
  };
  return (
    <div className="relative">
      {label && <label className="mb-1.5 block text-sm font-medium">{label}</label>}
      <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
      <TextInput
        role="combobox"
        aria-expanded={open && items.length > 0}
        aria-controls={listId}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={q}
        className="pl-9"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, items.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter' && items[active]) {
            e.preventDefault();
            pick(items[active]);
          } else if (e.key === 'Escape') setOpen(false);
        }}
      />
      </div>
      {open && dq && (
        <ul id={listId} role="listbox" className="absolute z-30 mt-1 max-h-80 w-full overflow-y-auto rounded-[10px] border border-line bg-surface py-1 shadow-xl">
          {res.isLoading && <li className="px-3 py-2 text-sm text-muted">Searching…</li>}
          {!res.isLoading && items.length === 0 && <li className="px-3 py-2 text-sm text-muted">No products match “{dq}”</li>}
          {items.map((p, i) => (
            <li
              key={p.id}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(p);
              }}
              onMouseEnter={() => setActive(i)}
              className={clsx('flex cursor-pointer items-center justify-between gap-3 px-3 py-2', i === active && 'bg-ledger-50')}
            >
              <span className="min-w-0">
                <span className="block truncate font-medium">{p.name}</span>
                <span className="block text-xs text-muted">
                  {p.sku}
                  {p.stock ? ` — ${formatQty(p.stock.qtyMilli)} in stock` : ''}
                </span>
              </span>
              <span className="text-sm num">{(p.priceCents / 100).toFixed(2)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
